import { TrendingDown, TrendingUp } from 'lucide-react';
import { DashPanel } from './DashPanel';
import { DataCardHeader } from './CardExportButton';
import { cn } from '@/lib/utils';

export interface RankedListRow {
  label: string;
  value: string;
  trendPct?: number;
  /** Participação 0–100 — quando presente, desenha a barra proporcional do design system (8px, rounded-full). */
  pct?: number;
}

const MEDALS = {
  1: { className: 'bg-amber-400' },
  2: { className: 'bg-slate-400' },
  3: { className: 'bg-orange-700' },
} as const;

export function RankedList({
  title,
  rows,
  emptyLabel = 'Sem dados suficientes ainda',
  onExport,
}: {
  title: string;
  rows: RankedListRow[];
  emptyLabel?: string;
  onExport?: () => void;
}) {
  return (
    <DashPanel padding="sm">
      <DataCardHeader title={title} onExport={onExport} />
      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground py-4 text-center">{emptyLabel}</p>
      ) : (
        <ol className="max-h-[280px] overflow-y-auto pr-1 scroll-styled">
          {rows.map((row, idx) => {
            const position = idx + 1;
            const medal = MEDALS[position as keyof typeof MEDALS];
            return (
              <li
                key={`${row.label}-${idx}`}
                className="pt-3 border-t border-border first:border-0 first:pt-0"
              >
                <div className="flex items-center gap-3">
                  {medal ? (
                    <span
                      className={cn(
                        'flex items-center justify-center w-4 h-4 shrink-0 rounded-full text-[10px] font-semibold text-white',
                        medal.className
                      )}
                    >
                      {position}
                    </span>
                  ) : (
                    <span className="text-[11px] text-muted-foreground w-4 shrink-0 tabular-nums text-center">
                      {position}
                    </span>
                  )}
                  <span className="text-sm text-foreground flex-1 truncate">{row.label}</span>
                  <span className="text-sm font-medium text-foreground tabular-nums">
                    {row.value}
                  </span>
                  {typeof row.trendPct === 'number' && (
                    <span
                      className={cn(
                        'flex items-center gap-0.5 text-xs font-medium tabular-nums',
                        row.trendPct >= 0 ? 'text-emerald-600' : 'text-destructive'
                      )}
                    >
                      {row.trendPct >= 0 ? (
                        <TrendingUp className="h-3 w-3" />
                      ) : (
                        <TrendingDown className="h-3 w-3" />
                      )}
                      {Math.abs(row.trendPct).toFixed(0)}%
                    </span>
                  )}
                </div>
                {typeof row.pct === 'number' && (
                  <div className="h-2 rounded-full bg-muted mt-1.5 ml-7">
                    <div
                      className="h-full rounded-full bg-primary"
                      style={{ width: `${Math.max(0, Math.min(100, row.pct))}%` }}
                    />
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </DashPanel>
  );
}
